import React from 'react';
import { Box, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { SAGE, CREAM } from '../../theme';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

/**
 * WidgetShell — chrome around any dashboard widget.
 *
 * Header carries the widget kind label + title + (optional) "the bet" line
 * — the load-bearing concept from the handoff doc. Body is whatever the
 * widget renders. Footer has the primary CTA.
 *
 * Hover state inverts the card to dark sage (matching the landing page's
 * dark-green button language) — cream text on sage background. Gives the
 * dashboard a tactile "browsing" feel where cards light up under the cursor.
 *
 * The three-dot menu is visual-only for Phase 0 (Layer 1 customization,
 * deferred until per-widget settings are needed).
 */
const WidgetShell = ({ kind, title, bet, footerCta, onFooterClick, children, primary, accent = SAGE[600] }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: CREAM[50],
        borderRadius: 3,
        border: `2px solid ${primary ? accent : SAGE[200]}`,
        boxShadow: primary ? `0 4px 16px -4px ${accent}40` : 'none',
        overflow: 'hidden',
        cursor: onFooterClick ? 'pointer' : 'default',
        transition: 'background-color 0.18s, border-color 0.18s, transform 0.18s, box-shadow 0.18s, color 0.18s',
        '& .widget-menu': { opacity: 0, transition: 'opacity 0.15s' },
        '&:hover .widget-menu': { opacity: 1 },
        '&:hover': onFooterClick ? {
          bgcolor: SAGE[800],
          borderColor: SAGE[800],
          color: CREAM[50],
          transform: 'translateY(-2px)',
          boxShadow: `0 10px 30px -6px ${SAGE[800]}55`,
          '& .widget-kind':   { color: CREAM[200] },
          '& .widget-title':  { color: CREAM[50] },
          '& .widget-bet':    { color: CREAM[200] },
          '& .widget-menu':   { color: CREAM[200] },
          '& .widget-footer': {
            bgcolor: 'transparent',
            borderTopColor: `${CREAM[50]}30`,
          },
          '& .widget-footer-text': { color: CREAM[50] },
          '& .widget-footer-arrow': { color: CREAM[50] },
        } : {},
      }}
      onClick={onFooterClick}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.75 }}>
          {kind && (
            <Typography
              className="widget-kind"
              sx={{
                fontFamily: F,
                fontSize: '0.62rem',
                fontWeight: 700,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: accent,
                transition: 'color 0.18s',
              }}
            >
              {kind}
            </Typography>
          )}
          <Box className="widget-menu" sx={{ color: SAGE[400], cursor: 'pointer', display: 'flex' }}>
            <MoreHorizIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>
        {title && (
          <Typography
            className="widget-title"
            sx={{
              fontFamily: SERIF,
              fontSize: '1.1rem',
              fontWeight: 500,
              color: SAGE[800],
              lineHeight: 1.3,
              mb: bet ? 1 : 0,
              transition: 'color 0.18s',
            }}
          >
            {title}
          </Typography>
        )}
        {bet && (
          <Typography
            className="widget-bet"
            sx={{
              fontFamily: SERIF,
              fontSize: '0.78rem',
              fontStyle: 'italic',
              color: SAGE[500],
              lineHeight: 1.5,
              transition: 'color 0.18s',
            }}
          >
            "{bet}"
          </Typography>
        )}
      </Box>

      {children && (
        <Box sx={{ px: 2.5, pb: 2, flex: 1, borderTop: `1px solid ${SAGE[100]}`, pt: 2 }}>{children}</Box>
      )}

      {footerCta && (
        <Box
          className="widget-footer"
          sx={{
            px: 2.5,
            py: 1.25,
            borderTop: `1px solid ${SAGE[100]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: primary ? `${accent}10` : 'transparent',
            transition: 'background-color 0.18s, border-color 0.18s',
          }}
        >
          <Typography
            className="widget-footer-text"
            sx={{ fontFamily: F, fontSize: '0.78rem', fontWeight: 600, color: accent, transition: 'color 0.18s' }}
          >
            {footerCta}
          </Typography>
          <Box className="widget-footer-arrow" sx={{ color: accent, display: 'flex', transition: 'color 0.18s' }}>→</Box>
        </Box>
      )}
    </Box>
  );
};

export default WidgetShell;
